/*
 * Назначение: страница «Заявки».
 * Функционал: таблица заявок с фильтрами и поиском, изменение статуса и
 * заметки в карточке, экспорт в CSV. Все данные — из /api/admin/leads.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Download,
  ExternalLink,
  Loader2,
  Paperclip,
  Search,
} from "lucide-react";
import { toast } from "sonner";

import { leadsApi } from "../../lib/admin-api";
import { ApiError } from "../../lib/api";
import {
  CHANNEL_LABEL,
  formatDateTime,
  LEAD_STATUS_LABEL,
} from "../../lib/format";
import type { Lead, LeadStatus } from "../../lib/types";
import { useProject } from "../components/project/project-provider";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";

const STATUS_VARIANT: Record<
  LeadStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  new: "destructive",
  in_progress: "default",
  won: "outline",
  lost: "secondary",
};

const PAGE_SIZE = 25;

export function LeadsPage() {
  const { current, currentSlug } = useProject();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Lead | null>(null);
  const [editStatus, setEditStatus] = useState<LeadStatus>("new");
  const [editNotes, setEditNotes] = useState("");

  const queryKey = [
    "leads",
    currentSlug,
    { search, statusFilter, channelFilter, page },
  ];

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () =>
      leadsApi.list({
        page,
        page_size: PAGE_SIZE,
        search: search.trim() || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        channel: channelFilter !== "all" ? channelFilter : undefined,
      }),
    enabled: !!currentSlug,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: string; status: LeadStatus; notes: string }) =>
      leadsApi.update(id, { status, notes: notes || null }),
    onSuccess: (lead) => {
      toast.success("Заявка обновлена");
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      setSelected(lead);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.detail : "Не удалось сохранить");
    },
  });

  const openLead = (lead: Lead) => {
    setSelected(lead);
    setEditStatus(lead.status);
    setEditNotes(lead.notes || "");
  };

  const closeLead = () => {
    setSelected(null);
  };

  const handleExport = async () => {
    try {
      const res = await fetch(leadsApi.exportCsvUrl(), {
        credentials: "include",
        headers: currentSlug ? { "X-Project-Slug": currentSlug } : {},
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads-${currentSlug ?? "project"}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Файл скачан");
    } catch (err) {
      toast.error(`Не удалось выгрузить: ${(err as Error).message}`);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const items = data?.items ?? [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Заявки</h2>
          <p className="text-sm text-muted-foreground">
            Обращения клиентов проекта {current ? `«${current.name}»` : ""}
          </p>
        </div>
        <Button variant="outline" onClick={handleExport}>
          <Download className="mr-2 h-4 w-4" />
          Экспорт в CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Поиск по имени, контакту, услуге..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setPage(1);
                setStatusFilter(v);
              }}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="new">Новые</SelectItem>
                <SelectItem value="in_progress">В работе</SelectItem>
                <SelectItem value="won">Успешно закрытые</SelectItem>
                <SelectItem value="lost">Без сделки</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={channelFilter}
              onValueChange={(v) => {
                setPage(1);
                setChannelFilter(v);
              }}
            >
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Канал" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все каналы</SelectItem>
                <SelectItem value="web">Сайт</SelectItem>
                <SelectItem value="telegram">Telegram</SelectItem>
                <SelectItem value="max">MAX</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Клиент</TableHead>
                  <TableHead>Контакт</TableHead>
                  <TableHead>Услуга</TableHead>
                  <TableHead>Канал</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Дата</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                      Загружаем…
                    </TableCell>
                  </TableRow>
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-destructive py-8"
                    >
                      <AlertCircle className="h-5 w-5 inline-block mr-2" />
                      Не удалось загрузить заявки
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center text-muted-foreground py-8"
                    >
                      Заявок пока нет. Они появятся, когда клиенты оставят
                      контакты в диалоге с ИИ-продавцом.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => openLead(lead)}
                    >
                      <TableCell className="font-mono text-xs">
                        {lead.id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {lead.name || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.contact || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.service || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{CHANNEL_LABEL[lead.channel]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_VARIANT[lead.status]}>
                          {LEAD_STATUS_LABEL[lead.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(lead.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLead(lead);
                          }}
                        >
                          Открыть
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              {data
                ? `Показано ${items.length} из ${data.total}, страница ${data.page} из ${totalPages}`
                : ""}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Предыдущая
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!data || page >= totalPages || isLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                Следующая
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && closeLead()}>
        <DialogContent
          className="max-w-2xl"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              Заявка {selected ? selected.id.slice(0, 8) : ""}
            </DialogTitle>
            <DialogDescription>Подробности и управление статусом</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Клиент</Label>
                  <p className="text-sm font-medium mt-1">
                    {selected.name || "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Контакт</Label>
                  <p className="text-sm font-medium mt-1">
                    {selected.contact || "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Услуга</Label>
                  <p className="text-sm font-medium mt-1">
                    {selected.service || "—"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Канал</Label>
                  <Badge variant="outline" className="mt-1">
                    {CHANNEL_LABEL[selected.channel]}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Описание</Label>
                  <p className="text-sm mt-1 whitespace-pre-line">
                    {selected.description || "—"}
                  </p>
                </div>
                {selected.attachments && selected.attachments.length > 0 && (
                  <div className="col-span-2">
                    <Label className="text-muted-foreground flex items-center gap-1.5">
                      <Paperclip className="h-3.5 w-3.5" />
                      Файлы клиента ({selected.attachments.length})
                    </Label>
                    <ul className="mt-2 space-y-1.5">
                      {selected.attachments.map((att) => (
                        <li key={att.id} className="text-sm">
                          <a
                            href={att.download_url}
                            className="text-primary hover:underline break-all"
                            download
                          >
                            {att.filename}
                          </a>
                          <span className="text-muted-foreground text-xs ml-2">
                            {att.size_bytes < 1024 * 1024
                              ? `${Math.max(Math.round(att.size_bytes / 1024), 1)} КБ`
                              : `${(att.size_bytes / 1024 / 1024).toFixed(1)} МБ`}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Файлы — заказы клиента на расчёт. Нейросеть их не открывает.
                    </p>
                  </div>
                )}
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Создана</Label>
                  <p className="text-sm font-medium mt-1">
                    {formatDateTime(selected.created_at)}
                  </p>
                </div>
              </div>

              {selected.dialog_id && (
                <div className="border-t pt-4">
                  <Label className="text-muted-foreground">Источник</Label>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="mt-2 h-auto p-3 w-full justify-start"
                  >
                    <a
                      href={`/dialogs/${selected.dialog_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Открыть диалог {selected.dialog_id.slice(0, 8)} в новой вкладке
                    </a>
                  </Button>
                </div>
              )}

              <div className="border-t pt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="lead-status">Статус</Label>
                  <Select
                    value={editStatus}
                    onValueChange={(v) => setEditStatus(v as LeadStatus)}
                  >
                    <SelectTrigger id="lead-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Новая</SelectItem>
                      <SelectItem value="in_progress">В работе</SelectItem>
                      <SelectItem value="won">Успешно закрыта</SelectItem>
                      <SelectItem value="lost">Без сделки</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lead-notes">Заметка</Label>
                  <Textarea
                    id="lead-notes"
                    rows={3}
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Кратко: что обсудили, что обещали клиенту"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={closeLead}>
              Закрыть
            </Button>
            <Button
              onClick={() => {
                if (selected) {
                  updateMutation.mutate({
                    id: selected.id,
                    status: editStatus,
                    notes: editNotes,
                  });
                }
              }}
              disabled={updateMutation.isPending || !selected}
            >
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
